import { refreshErp } from "@/core/api/erp-auth";
import {
  clearStoredAuthUser,
  isAccessTokenStale,
  readStoredAuthUser,
  replaceStoredTokens,
  writeStoredAuthUser
} from "@/core/auth/session";
import { mergeAuthUserProfile } from "@/core/auth/profile";

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:4000/api/v1";

export function resolveErpAssetUrl(path?: string) {
  const value = path?.trim();
  if (!value) return "";
  if (/^(?:https?:|blob:|data:)/i.test(value)) return value;

  try {
    const fallbackOrigin =
      typeof window === "undefined"
        ? "http://localhost"
        : window.location.origin;
    const apiUrl = new URL(API_BASE_URL, fallbackOrigin);
    const assetPath = value.startsWith("/") ? value : `/${value}`;
    return new URL(assetPath, `${apiUrl.origin}/`).toString();
  } catch {
    return value;
  }
}

type ApiErrorPayload = {
  message?: string | string[];
  error?: string;
  code?: string;
  error_code?: string;
};

export class ErpApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ErpApiError";
    this.status = status;
    this.code = code;
  }
}

let refreshPromise: Promise<string> | null = null;

function joinUrl(baseUrl: string, path: string) {
  return `${baseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
}

function messageFromPayload(payload: unknown, fallback: string) {
  const errorPayload = payload as ApiErrorPayload;
  if (Array.isArray(errorPayload?.message)) {
    return errorPayload.message.join(", ");
  }
  return errorPayload?.message || errorPayload?.error || fallback;
}

function isFormDataBody(body: BodyInit | null | undefined) {
  return typeof FormData !== "undefined" && body instanceof FormData;
}

async function readJson(response: Response) {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function redirectToLogin() {
  if (typeof window === "undefined") return;
  const { pathname, search } = window.location;
  if (pathname === "/login") return;

  const redirect = `${pathname}${search}`;
  window.location.assign(`/login?redirect=${encodeURIComponent(redirect)}`);
}

async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const session = readStoredAuthUser();
    if (!session?.refreshToken) {
      throw new ErpApiError("Phiên đăng nhập đã hết hạn", 401);
    }

    const tokens = await refreshErp(session.refreshToken);
    const tokenUser = replaceStoredTokens(tokens);
    if (tokenUser && tokens.user) {
      writeStoredAuthUser(mergeAuthUserProfile(tokenUser, tokens.user));
    }
    return tokens.accessToken;
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

async function getValidAccessToken() {
  const session = readStoredAuthUser();
  if (!session?.accessToken) {
    throw new ErpApiError("Phiên đăng nhập đã hết hạn", 401);
  }

  if (isAccessTokenStale(session)) {
    return refreshAccessToken();
  }

  return session.accessToken;
}

async function fetchWithToken(
  path: string,
  token: string,
  init: RequestInit = {}
) {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");

  if (init.body && !isFormDataBody(init.body) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  headers.set("Authorization", `Bearer ${token}`);

  return fetch(joinUrl(API_BASE_URL, path), {
    ...init,
    headers
  });
}

async function executeErpRequest(path: string, init: RequestInit = {}) {
  let token: string;

  try {
    token = await getValidAccessToken();
  } catch (error) {
    clearStoredAuthUser();
    redirectToLogin();
    throw error;
  }

  let response = await fetchWithToken(path, token, init);

  if (response.status === 401) {
    try {
      const refreshedToken = await refreshAccessToken();
      response = await fetchWithToken(path, refreshedToken, init);
    } catch (error) {
      clearStoredAuthUser();
      redirectToLogin();
      throw error;
    }
  }

  if (!response.ok) {
    const payload = await readJson(response);
    if (response.status === 401) {
      clearStoredAuthUser();
      redirectToLogin();
    }

    throw new ErpApiError(
      messageFromPayload(payload, "Không thể kết nối hệ thống"),
      response.status,
      (payload as ApiErrorPayload | null)?.code ||
        (payload as ApiErrorPayload | null)?.error_code
    );
  }

  return response;
}

export async function erpRequest<T>(path: string, init: RequestInit = {}) {
  const response = await executeErpRequest(path, init);
  const payload = await readJson(response);

  return payload as T;
}

export type ErpFileDownload = {
  blob: Blob;
  fileName?: string;
  contentType?: string | null;
};

function getFileName(contentDisposition: string | null) {
  if (!contentDisposition) return undefined;
  const utf8Name = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (utf8Name) return decodeURIComponent(utf8Name);
  return contentDisposition.match(/filename=\"?([^\";]+)\"?/i)?.[1];
}

export async function erpDownload(
  path: string,
  init: RequestInit = {}
): Promise<ErpFileDownload> {
  const response = await executeErpRequest(path, init);
  return {
    blob: await response.blob(),
    fileName: getFileName(response.headers.get("Content-Disposition")),
    contentType: response.headers.get("Content-Type")
  };
}
